// Security Service - PCI Compliance token and session management
import { brandConfig } from '@/config/brandConfig';
import secureCookieService from './secureCookieService.js';

class SecurityService {
  constructor() {
    this.sessionAttempts = new Map();
    this.maxAttempts = 2;
    this.attemptWindow = 5000; // 5 seconds
  }

  // Generate secure session token (no UUID on frontend)
  generateSessionToken() {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2);
    return btoa(`${timestamp}_${random}`);
  }

  // Validate session with retry logic
  async validateSession(token, retryCount = 0) {
    const key = `session_${token}`;
    const now = Date.now();
    
    // Check attempt limits
    if (this.sessionAttempts.has(key)) {
      const attempts = this.sessionAttempts.get(key);
      if (attempts.count >= this.maxAttempts && (now - attempts.firstAttempt) < this.attemptWindow) {
        throw new Error('Too many validation attempts. Please wait 5 seconds.');
      }
      if ((now - attempts.firstAttempt) >= this.attemptWindow) {
        this.sessionAttempts.delete(key);
      }
    }

    try {
      // Record attempt
      if (!this.sessionAttempts.has(key)) {
        this.sessionAttempts.set(key, { count: 1, firstAttempt: now });
      } else {
        const attempts = this.sessionAttempts.get(key);
        attempts.count++;
      }

      // Validate with central proxy
      const response = await fetch('https://api-frankfurt.rezcomm.com/central/auth/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'validate', token })
      });

      if (response.ok) {
        this.sessionAttempts.delete(key);
        return await response.json();
      }
      
      throw new Error('Session validation failed');
    } catch (error) {
      if (retryCount < this.maxAttempts - 1) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return this.validateSession(token, retryCount + 1);
      }
      throw error;
    }
  }



  // Get brand configuration securely
  getBrandConfig(brandCode) {
    return brandConfig.getBrand(brandCode);
  }

  // Secure logout - clear all session data
  secureLogout() {
    // Clear session storage
    sessionStorage.clear();
    
    // Clear local storage auth data
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_session');
    
    // Clear secure cookies
    secureCookieService.clearAuth();
  }

  // Tamper-proof session check
  validateSessionIntegrity(token, userEmail = null) {
    try {
      // For initial brand-only tokens, just validate basic format and age
      if (!token) return false;
      
      // Basic token format validation - handle JWT tokens from auth.mjs
      if (token.includes('.')) {
        // JWT token - just check it exists and is not expired
        const parts = token.split('.');
        if (parts.length !== 3) return false;
        
        try {
          const payload = JSON.parse(atob(parts[1]));
          // Check if token is expired
          if (payload.exp && Date.now() / 1000 > payload.exp) {
            return false;
          }
          return true;
        } catch {
          return false;
        }
      }
      
      // Legacy token format
      const decoded = atob(token.split('_')[0] || '');
      const timestamp = parseInt(decoded.split('_')[0]);
      
      // Check if token is too old (24 hours)
      if (Date.now() - timestamp > 86400000) {
        return false;
      }
      
      return true;
    } catch {
      return false;
    }
  }
}

export default new SecurityService();